import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Star, Flame, MapPin, Tag, Users, Plus, Sparkles } from 'lucide-react';

/**
 * PartnerCard — Q34.1 Explore → Partners feed card.
 *
 * Consistent with PlanCard's "cover image → meta → actions" rhythm so the
 * Plans / People / Communities / Partners tabs all feel like the same
 * surface. Cards render even without imagery (gradient fallback +
 * monogram). Tap the card body → /business/<slug-or-id>.
 */
export default function PartnerCard({ partner }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const goProfile = () => navigate(`/business/${partner.slug || partner.id}`);
  const goCreatePlan = () => {
    setCreating(true);
    // Forwards into the existing CreatePlan flow with the partner pre-selected.
    navigate(`/create?partner=${partner.id}`);
  };

  const types = (partner.business_types || []).slice(0, 3);

  return (
    <article
      data-testid={`partner-card-${partner.id}`}
      className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden active:scale-[0.995] transition-transform"
    >
      {/* Cover */}
      <button
        data-testid={`partner-cover-${partner.id}`}
        onClick={goProfile}
        className="relative w-full aspect-[16/9] block bg-gradient-to-br from-[#3F5200] to-[#B6E600] text-left overflow-hidden"
      >
        {partner.cover_image ? (
          <img
            src={partner.cover_image}
            alt={partner.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/40 text-4xl font-extrabold">
            {initials(partner.name)}
          </div>
        )}

        {/* Badges row (top-right) */}
        <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
          {partner.badges?.verified && (
            <span data-testid="badge-verified" className="bg-white/95 backdrop-blur-sm text-[#3F5200] text-[10px] font-extrabold px-2 py-1 rounded-full flex items-center gap-1">
              <CheckCircle2 size={11} strokeWidth={2.8} /> Verified
            </span>
          )}
          {partner.badges?.featured && (
            <span data-testid="badge-featured" className="bg-[#B6E600] text-[#111] text-[10px] font-extrabold px-2 py-1 rounded-full flex items-center gap-1">
              <Sparkles size={11} strokeWidth={2.8} /> Featured
            </span>
          )}
          {partner.badges?.top_rated && (
            <span data-testid="badge-top-rated" className="bg-[#FF6B35] text-white text-[10px] font-extrabold px-2 py-1 rounded-full flex items-center gap-1">
              <Flame size={11} strokeWidth={2.8} /> Top Rated
            </span>
          )}
        </div>

        {/* Logo (bottom-left) */}
        {partner.logo_image && (
          <img
            src={partner.logo_image}
            alt=""
            className="absolute -bottom-5 left-3 w-12 h-12 rounded-xl border-2 border-white bg-white object-cover shadow"
          />
        )}
      </button>

      {/* Body */}
      <div className={`p-3 ${partner.logo_image ? 'pt-8' : 'pt-3'}`}>
        <button onClick={goProfile} className="block text-left w-full">
          <h3 className="text-base font-extrabold text-[#111] leading-tight">{partner.name}</h3>
          <p className="text-[11px] text-[#3F5200] font-bold uppercase tracking-wider mt-0.5">Partner venue</p>
        </button>

        {/* Meta row — categories + city */}
        <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[11px] text-[#6B6E73]">
          {types.length > 0 && (
            <span data-testid={`partner-types-${partner.id}`} className="flex items-center gap-1">
              <Tag size={11} />
              {types.join(' · ')}
            </span>
          )}
          {(partner.area || partner.city) && (
            <span className="flex items-center gap-1">
              <MapPin size={11} />
              {partner.area ? `${partner.area}, ${partner.city}` : partner.city}
            </span>
          )}
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-3 mt-2 text-[11px] text-[#3F5200] font-bold">
          {partner.offers_count > 0 && (
            <span data-testid={`partner-offers-count-${partner.id}`} className="flex items-center gap-1">
              <Sparkles size={11} strokeWidth={2.8} /> {partner.offers_count} active {partner.offers_count === 1 ? 'offer' : 'offers'}
            </span>
          )}
          {partner.groups_hosted > 0 && (
            <span data-testid={`partner-groups-${partner.id}`} className="flex items-center gap-1">
              <Users size={11} strokeWidth={2.8} /> {partner.groups_hosted} {partner.groups_hosted === 1 ? 'group' : 'groups'}
            </span>
          )}
          {partner.rating > 0 && (
            <span className="flex items-center gap-1 text-[#FFB400]">
              <Star size={11} fill="currentColor" strokeWidth={0} /> {partner.rating.toFixed(1)}
            </span>
          )}
        </div>

        {/* Offers preview (max 2) */}
        {partner.offers_preview && partner.offers_preview.length > 0 && (
          <div data-testid={`partner-offers-preview-${partner.id}`} className="mt-3 space-y-1.5">
            {partner.offers_preview.slice(0, 2).map(o => (
              <div
                key={o.id}
                className="flex items-center gap-2 bg-[#B6E600]/10 rounded-lg p-2 border border-[#B6E600]/20"
              >
                <span className="text-[#3F5200]"><Sparkles size={12} strokeWidth={2.8} /></span>
                <span className="text-xs font-bold text-[#111] flex-1 truncate">{o.title}</span>
                {o.min_attendees > 1 && (
                  <span className="text-[10px] text-[#3F5200] font-bold">{o.min_attendees}+</span>
                )}
              </div>
            ))}
            {partner.offers_count > 2 && (
              <button
                onClick={goProfile}
                data-testid={`partner-more-offers-${partner.id}`}
                className="text-[11px] text-[#3F5200] font-extrabold active:underline"
              >
                +{partner.offers_count - 2} more {partner.offers_count - 2 === 1 ? 'offer' : 'offers'}
              </button>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button
            data-testid={`partner-create-plan-${partner.id}`}
            onClick={goCreatePlan}
            disabled={creating}
            className="bg-[#B6E600] text-[#111] font-bold rounded-full py-2 text-xs flex items-center justify-center gap-1.5 active:scale-[0.97] transition-transform"
          >
            <Plus size={13} strokeWidth={2.8} /> Create plan here
          </button>
          <button
            data-testid={`partner-view-profile-${partner.id}`}
            onClick={goProfile}
            className="bg-[#F1F0EA] text-[#111] font-bold rounded-full py-2 text-xs active:scale-[0.97] transition-transform"
          >
            View profile
          </button>
        </div>
      </div>
    </article>
  );
}

function initials(name) {
  return (name || '?').split(' ').map(s => s[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();
}
